import { TitleLarge, TitleMedium, BodyRegular } from '@/components/typography';
import { TextBadge } from '@/components/elements';
import { LazyVideo } from '@/components/elements';
import IntroAnim from '@/assets/anim/home-intro.webm';
import RocketImage from '@/assets/img/ui/rocket.png';

export const About = () => {
    return (
        <div className="w-full">
            {/* Hero Section – layout to lg:pt-[29px] Home->Introduction */}
            <section className="relative w-full flex items-center justify-center overflow-hidden px-4 py-16 lg:min-h-screen">
                <div className="relative z-10 w-full max-w-[320px] lg:max-w-[1200px] flex flex-col items-center lg:flex-row lg:items-center gap-8 lg:gap-0">
                    <div className="w-full lg:flex-1 flex flex-col items-start gap-6 lg:gap-20 max-w-[326px] lg:max-w-[730px] lg:min-w-[736px]">
                        <div className="flex flex-col gap-4 w-full">
                            <TextBadge className='w-max'>About</TextBadge>
                            <TitleLarge className="text-white">
                                A Cultural <span className="text-primary">Ecosystem</span> with a Sense of Humour and a Plan.
                            </TitleLarge>
                        </div>
                        {/* Vision, Mission, Journey */}
                        <div className="w-full flex flex-col gap-6 lg:gap-10 max-w-[320px] lg:max-w-[492px]">
                            <div className="flex flex-col gap-4">
                                <TitleMedium className="text-white">
                                    Our <span className="text-primary">Vision</span>
                                </TitleMedium>
                                <BodyRegular className="text-white/80 lg:text-white/60">
                                    To redefine meme culture by merging entertainment, education, and sustainable tokenomics within a decentralised creative economy.
                                </BodyRegular>
                            </div>

                            <div className="flex flex-col gap-4">
                                <TitleMedium className="text-white">
                                    Our <span className="text-primary">Mission</span>
                                </TitleMedium>
                                <BodyRegular className="text-white/80 lg:text-white/60">
                                    To make crypto accessible and enjoyable—empowering individuals through humour, AI, and community-driven value.
                                </BodyRegular>
                            </div>

                            <div className="flex flex-col gap-4">
                                <TitleMedium className="text-white">
                                    Our <span className="text-primary">Journey</span>
                                </TitleMedium>
                                <BodyRegular className="text-white/80 lg:text-white/60">
                                    What began as satire has become a pioneering digital culture project. From live NFT auctions to governance-backed social initiatives, CCRAP blurs the line between art, comedy, and financial innovation.
                                </BodyRegular>
                            </div>
                        </div>
                    </div>
                    <div className="flex-1 flex items-center justify-center relative w-full order-2 min-h-[380px] lg:min-h-0">
                        <LazyVideo
                            loop={false}
                            src={IntroAnim}
                            className="absolute pointer-events-none scale-x-[-1] left-2/3 top-1/2 -translate-x-1/2 -translate-y-1/2 w-[280vw] max-w-[1180px] lg:max-w-[1598px] lg:w-auto lg:h-[898px]"
                        />
                    </div>
                </div>
            </section>

            {/* Roadmap Section */}
            <section className="w-full flex justify-center px-4 py-8 lg:py-16 relative">

                <div className="w-full max-w-[320px] lg:max-w-[1200px] flex flex-col items-start lg:items-center gap-8 lg:gap-12 relative">

                    {/* Fancy-ful vector line */}
                    <div className="absolute z-0 w-full -top-10 origin-top-left">
                        <svg xmlns="http://www.w3.org/2000/svg" className='w-full h-full' viewBox="0 0 1412 395" fill="none">
                            <path d="M1.50399 1.49667C12.588 24.3401 249.638 437.913 616.275 388.794C982.911 339.675 1260.52 205.725 1475.01 348.552" stroke="url(#paint0_linear_259_1560)" stroke-width="3" stroke-miterlimit="10" stroke-linecap="round"/>
                            <defs>
                                <linearGradient id="paint0_linear_259_1560" x1="1411.99" y1="520.706" x2="0.562399" y2="4.06905" gradientUnits="userSpaceOnUse">
                                    <stop offset="0.180308" stop-color="#89FF69" stop-opacity="0"/>
                                    <stop offset="0.5" stop-color="#89FF69"/>
                                    <stop offset="0.851263" stop-color="#89FF69" stop-opacity="0"/>
                                </linearGradient>
                            </defs>
                        </svg>
                    </div>
                    
                    <TitleMedium className="text-white lg:text-center relative z-10">
                        Roadmap
                    </TitleMedium>

                    {/* Roadmap Timeline - Mobile: vertical, Desktop: horizontal */}
                    <div className="w-full flex flex-col lg:grid lg:grid-cols-4 gap-6 lg:gap-8 relative z-10">
                        <div className="flex gap-4 lg:flex-col lg:items-center lg:pt-[29px]">
                            <div className="w-16 h-16 flex-shrink-0 relative bg-gradient-to-br from-white/20 to-white/5 rounded-[56px] shadow-[0px_0.96px_23.96px_0px_rgba(69,42,124,0.10)] border-2 border-white/10 backdrop-blur-xl flex items-center justify-center">
                                <div className="text-center text-primary text-base font-bold font-outfit leading-6">Q4 25</div>
                            </div>
                            <div className="flex-1 flex flex-col gap-4">
                                <div className="text-primary text-base lg:text-xl font-medium font-outfit lg:text-center">Pre-Launch</div>
                                <div className="flex flex-col gap-2">
                                    <TextBadge textClassName='text-white'>ICO launch</TextBadge>
                                    <TextBadge textClassName="text-white">Staking pools</TextBadge>
                                    <TextBadge textClassName="text-white">Marketing campaigns</TextBadge>
                                    <TextBadge textClassName="text-white">Audit prep</TextBadge>
                                </div>
                            </div>
                        </div>

                        <div className="flex gap-4 lg:flex-col lg:items-center lg:pt-[168px]">
                            <div className="w-16 h-16 flex-shrink-0 relative bg-gradient-to-br from-white/20 to-white/5 rounded-[56px] shadow-[0px_0.96px_23.96px_0px_rgba(69,42,124,0.10)] border-2 border-white/10 backdrop-blur-xl flex items-center justify-center">
                                <div className="text-center text-primary text-base font-bold font-outfit leading-6">Q1 26</div>
                            </div>
                            <div className="flex-1 flex flex-col gap-4">
                                <div className="text-primary text-base lg:text-xl font-medium font-outfit lg:text-center">Platform Activation</div>
                                <div className="flex flex-col gap-2">
                                    <TextBadge textClassName="text-white">First live AI NFT auctions</TextBadge>
                                    <TextBadge textClassName="text-white">Marketplace launch</TextBadge>
                                    <TextBadge textClassName="text-white">DEX listings</TextBadge>
                                </div>
                            </div>
                        </div>

                        <div className="flex gap-4 lg:flex-col lg:items-center lg:pt-[132px]">
                            <div className="w-16 h-16 flex-shrink-0 relative bg-gradient-to-br from-white/20 to-white/5 rounded-[56px] shadow-[0px_0.96px_23.96px_0px_rgba(69,42,124,0.10)] border-2 border-white/10 backdrop-blur-xl flex items-center justify-center">
                                <div className="text-center text-primary text-base font-bold font-outfit leading-6">Q1-2 26</div>
                            </div>
                            <div className="flex-1 flex flex-col gap-4">
                                <div className="text-primary text-base lg:text-xl font-medium font-outfit lg:text-center">Growth Phase</div>
                                <div className="flex flex-col gap-2">
                                    <TextBadge textClassName="text-white">NFT fusion</TextBadge>
                                    <TextBadge textClassName="text-white">Third-party marketplace</TextBadge>
                                    <TextBadge textClassName="text-white">CEX listings</TextBadge>
                                </div>
                            </div>
                        </div>

                        <div className="flex gap-4 lg:flex-col lg:items-center lg:pt-[68px]">
                            <div className="w-16 h-16 flex-shrink-0 relative bg-gradient-to-br from-white/20 to-white/5 rounded-[56px] shadow-[0px_0.96px_23.96px_0px_rgba(69,42,124,0.10)] border-2 border-white/10 backdrop-blur-xl flex items-center justify-center">
                                <div className="text-center text-primary text-base font-bold font-outfit leading-6">Q3 26</div>
                            </div>
                            <div className="flex-1 flex flex-col gap-4">
                                <div className="text-primary text-base lg:text-xl font-medium font-outfit lg:text-center">Beyond</div>
                                <div className="flex flex-col gap-2">
                                    <TextBadge textClassName="text-white">Education Fund</TextBadge>
                                    <TextBadge textClassName="text-white">Anime media expansion</TextBadge>
                                    <TextBadge textClassName="text-white">Governance-led partnerships</TextBadge>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </section>

            {/* Future Expansion Section */}
            <section className="w-full flex justify-center px-4 py-8 lg:py-16">
                <div className="w-full max-w-[320px] lg:max-w-[1200px] flex flex-col lg:flex-row items-start gap-6 lg:gap-12">
                    <div className="w-full lg:flex-1 flex flex-col items-start gap-6">
                        <div className="text-2xl lg:text-4xl font-bold font-outfit">
                            <span className="text-white">Future </span>
                            <span className="text-primary">Expansion</span>
                        </div>
                        <BodyRegular className="text-white/80 lg:text-white/60 max-w-[320px] lg:max-w-[484px]">
                            CCRAP plans to expand into digital media by converting select community-created comics, manga, and manhua into short-form anime. These projects will use the AI Generative Engine and character repository to tell crypto-inspired stories—extending CCRAP's humour and identity beyond the blockchain.
                        </BodyRegular>
                    </div>
                    <div className="relative w-full lg:flex-1 h-52 lg:h-full">
                        {/* Rockets */}
                        <img className="w-40 lg:w-48 top-0 lg:-top-12 -right-12 lg:right-0 absolute rotate-[-6.31deg] animate-bounce [animation-duration:3s]" src={RocketImage} alt="Future Expansion background image" />
                        <img className="w-36 lg:w-48 -left-8 top-24 lg:top-20 lg:left-0 absolute rotate-[4.94deg] animate-bounce [animation-duration:3s]" src={RocketImage} alt="Future Expansion background image" />
                        <img className="w-64 lg:w-96 left-4 top-2 lg:-top-28 lg:left-20 absolute rotate-[1.45deg] animate-bounce [animation-duration:3s] [animation-delay:2s]" src={RocketImage} alt="Future Expansion background image" />
                        {/* Glow effect */}
                        <div className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 w-96 h-96 opacity-60 bg-cyan-400 rounded-full blur-[282.13px]" />
                    </div>
                </div>
            </section>
        </div>
    );
}